import { caseStudyGermanDetails } from "./caseStudyGermanDetails";
import { caseStudyFrenchDetails } from "./caseStudyFrenchDetails";
import { caseStudyItalianDetails } from "./caseStudyItalianDetails";
import { caseStudyItalianMetadata } from "./caseStudyItalianMetadata";
import { caseStudySpanishDetails } from "./caseStudySpanishDetails";
import { caseStudySpanishMetadata } from "./caseStudySpanishMetadata";

export interface CaseStudy {
  slug: string;
  title: string;
  titleDe?: string;
  titleFr?: string;
  titleIt?: string;
  client: string;
  clientTitle: string;
  industry: string;
  location: string;
  duration: string;
  services: string[];
  tags: string[];
  tagsDe?: string[];
  tagsFr?: string[];
  tagsIt?: string[];
  summary: string;
  summaryDe?: string;
  summaryFr?: string;
  summaryIt?: string;
  challenge: string;
  solution: string;
  impact: string[];
  technologies: string[];
  quote?: string;
  quoteAuthor?: string;
  quoteRole?: string;
  rating: number;
}

export const CASE_STUDY_TAGS = [
  "AI Strategy",
  "AI Development",
  "Generative AI",
  "RAG",
  "LLM Fine-Tuning",
  "Computer Vision",
  "Bioinformatics",
  "Pharma & Healthcare",
  "Banking & Finance",
  "Enterprise & Government",
  "Sales & Marketing",
  "Consulting",
] as const;

export type CaseStudyTag = (typeof CASE_STUDY_TAGS)[number];

export type CaseStudyLanguage = "en" | "de" | "fr" | "it" | "es";

export type LocalizedCaseStudy = Omit<CaseStudy, "title" | "summary" | "tags"> & {
  title: string;
  summary: string;
  tags: string[];
};

const CASE_STUDY_TAG_LABELS: Record<CaseStudyTag, Record<CaseStudyLanguage, string>> = {
  "AI Strategy": {
    en: "AI Strategy",
    de: "KI-Strategie",
    fr: "Stratégie IA",
    it: "Strategia IA",
    es: "Estrategia de IA",
  },
  "AI Development": {
    en: "AI Development",
    de: "KI-Entwicklung",
    fr: "Développement IA",
    it: "Sviluppo AI",
    es: "Desarrollo de IA",
  },
  "Generative AI": {
    en: "Generative AI",
    de: "Generative KI",
    fr: "IA générative",
    it: "AI generativa",
    es: "IA generativa",
  },
  RAG: {
    en: "RAG",
    de: "RAG",
    fr: "RAG",
    it: "RAG",
    es: "RAG",
  },
  "LLM Fine-Tuning": {
    en: "LLM Fine-Tuning",
    de: "LLM-Fine-Tuning",
    fr: "Fine-tuning de LLM",
    it: "Fine-tuning di LLM",
    es: "Fine-tuning de LLM",
  },
  "Computer Vision": {
    en: "Computer Vision",
    de: "Computer Vision",
    fr: "Vision par ordinateur",
    it: "Computer vision",
    es: "Visión por computador",
  },
  Bioinformatics: {
    en: "Bioinformatics",
    de: "Bioinformatik",
    fr: "Bioinformatique",
    it: "Bioinformatica",
    es: "Bioinformática",
  },
  "Pharma & Healthcare": {
    en: "Pharma & Healthcare",
    de: "Pharma & Gesundheitswesen",
    fr: "Pharma & santé",
    it: "Pharma e sanità",
    es: "Pharma y salud",
  },
  "Banking & Finance": {
    en: "Banking & Finance",
    de: "Banken & Finanzen",
    fr: "Banque & finance",
    it: "Banking e finanza",
    es: "Banca y finanzas",
  },
  "Enterprise & Government": {
    en: "Enterprise & Government",
    de: "Enterprise & öffentlicher Sektor",
    fr: "Entreprise & secteur public",
    it: "Enterprise e settore pubblico",
    es: "Enterprise y sector público",
  },
  "Sales & Marketing": {
    en: "Sales & Marketing",
    de: "Vertrieb & Marketing",
    fr: "Ventes & marketing",
    it: "Vendite e marketing",
    es: "Ventas y marketing",
  },
  Consulting: {
    en: "Consulting",
    de: "Beratung",
    fr: "Conseil",
    it: "Consulenza",
    es: "Consultoría",
  },
};

function normalizeCaseStudyLanguage(lang?: string): CaseStudyLanguage {
  const normalized = lang?.toLowerCase().split("-")[0];
  return normalized === "de" || normalized === "fr" || normalized === "it" || normalized === "es"
    ? normalized
    : "en";
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "deeptrait-ngs-data-analysis",
    title: "NGS Data Analysis Platform for Drug Discovery",
    titleDe: "NGS-Analyseplattform für die Wirkstoffforschung",
    titleFr: "Analyse NGS pour la découverte de médicaments",
    client: "DeepTrait",
    clientTitle: "Bioinformatics AI Startup (Co-founded by Sergii Shelpuk)",
    industry: "Pharma & Healthcare",
    location: "Germany / Ukraine",
    duration: "2018 – 2020",
    services: ["AI Development", "AI Consulting"],
    tags: ["AI Development", "Bioinformatics", "Pharma & Healthcare"],
    tagsDe: ["KI-Entwicklung", "Bioinformatik", "Pharma & Gesundheitswesen"],
    tagsFr: ["Développement IA", "Bioinformatique", "Pharma & santé"],
    summary:
      "Co-founded and built an AI-powered NGS data analysis platform that enables lab scientists to discover novel therapeutic targets and diagnostic markers in under six hours — with no coding, scripts, or command lines required.",
    summaryDe:
      "Mitgegründet und entwickelt: eine KI-gestützte NGS-Datenanalyseplattform, mit der Laborteams in weniger als sechs Stunden neue therapeutische Targets und diagnostische Marker entdecken können — ganz ohne Code, Skripte oder Kommandozeile.",
    summaryFr:
      "Cofondation et développement d'une plateforme d'analyse NGS propulsée par l'IA permettant aux scientifiques de laboratoire d'identifier de nouvelles cibles thérapeutiques et des biomarqueurs en moins de six heures, sans code ni scripts.",
    challenge:
      "Next-generation sequencing (NGS) data analysis required bioinformaticians with advanced programming skills. Lab scientists had to wait days for results and had no way to independently explore genomic datasets, run statistical tests, or annotate variants — creating critical bottlenecks in pre-clinical drug discovery pipelines.",
    solution:
      "We built DeepTrait — a no-code NGS data analysis platform designed by lab scientists, for lab scientists. The platform supports Kolmogorov-Smirnov statistical tests, ROC curve analysis, seamless variant and gene annotation across all major databases, and discovery of genetically supported therapeutic targets and diagnostic markers. Scientists go from raw data to actionable insights in under six hours.",
    impact: [
      "Reduced NGS data analysis time from days to under 6 hours",
      "Eliminated dependency on bioinformaticians for routine analyses",
      "Enabled discovery of novel therapeutic targets and diagnostic markers",
      "Supported all major variant and gene annotation databases",
    ],
    technologies: ["Bioinformatics", "NGS Analysis", "Machine Learning", "Statistical Testing", "Python", "Cloud Platform"],
    quote: "DeepTrait fundamentally changed how our lab scientists interact with genomic data.",
    quoteAuthor: "Dr. Ruslana Radchuk",
    quoteRole: "Co-founder & Chief Biologist, DeepTrait",
    rating: 5.0,
  },
  {
    slug: "astrazeneca-medical-affairs-ai",
    title: "AI Product for Medical Affairs",
    titleDe: "KI-Produkt für Medical Affairs",
    titleFr: "Produit IA pour les affaires médicales",
    client: "AstraZeneca",
    clientTitle: "Global Pharmaceutical Manufacturer",
    industry: "Pharma & Healthcare",
    location: "USA",
    duration: "Aug 2023 – Mar 2024",
    services: ["AI Development", "AI Consulting"],
    tags: ["AI Development", "Generative AI", "Pharma & Healthcare"],
    tagsDe: ["KI-Entwicklung", "Generative KI", "Pharma & Gesundheitswesen"],
    tagsFr: ["Développement IA", "IA générative", "Pharma & santé"],
    summary:
      "Developed an AI insights analytics tool and insurance flow pipeline for a global pharmaceutical company, automating CRM record analysis to deliver actionable insights from large-scale Medical Affairs operations.",
    summaryDe:
      "Entwickelt wurde ein KI-gestütztes Analytics-Tool für Insights sowie eine Pipeline für Medical Affairs, die CRM-Datensätze automatisiert analysiert und verwertbare Erkenntnisse aus groß angelegten Abläufen liefert.",
    summaryFr:
      "Développement d'un outil analytique d'insights IA et d'un pipeline pour les affaires médicales, automatisant l'analyse des données CRM afin de produire des insights actionnables à grande échelle.",
    challenge:
      "AstraZeneca needed to boost the efficiency of Medical Affairs analytics and unlock deeper insights from vast CRM data generated by their Medical Science Liaisons. The existing manual process was time-consuming and failed to surface actionable patterns.",
    solution:
      "We built an AI product that automates CRM record analysis, providing comprehensive overviews and delivering actionable insights from large-scale Medical Affairs operations. The solution included an insights flow pipeline that creates value for both field teams and HQ.",
    impact: [
      "Accelerated data-driven decision-making with far greater speed and precision",
      "Enhanced both commercial and operational effectiveness",
      "Transformed complex CRM data into a strategic asset",
      "Simplified workflows and unlocked new ways of using insights",
    ],
    technologies: ["Generative AI", "NLP", "Cloud Analytics", "CRM Integration"],
    quote: "They had a phenomenal knowledge of AI.",
    quoteAuthor: "Serhii Myroshnychenko",
    quoteRole: "Director of Brand Insights, AstraZeneca US",
    rating: 5.0,
  },
  {
    slug: "civitta-ai-accelerator-consulting",
    title: "AI Architecture for Generative AI Startups",
    titleDe: "KI-Architektur für Generative-AI-Startups",
    titleFr: "Architecture IA pour startups en IA générative",
    client: "Civitta",
    clientTitle: "International Consulting Company",
    industry: "Consulting",
    location: "Estonia",
    duration: "Jan 2024 – Jun 2024",
    services: ["AI Consulting"],
    tags: ["AI Strategy", "Consulting", "Generative AI"],
    tagsDe: ["KI-Strategie", "Beratung", "Generative KI"],
    tagsFr: ["Stratégie IA", "Conseil", "IA générative"],
    summary:
      "Provided AI tech advisory for four startups in the Challenger AI Accelerator, developing comprehensive AI product strategies and actionable implementation plans including system architecture designs.",
    summaryDe:
      "Technologieberatung für vier Startups im Challenger AI Accelerator mit umfassenden KI-Produktstrategien, umsetzbaren Implementierungsplänen und Systemarchitektur-Designs.",
    summaryFr:
      "Conseil technologique IA pour quatre startups du Challenger AI Accelerator, avec définition de stratégies produit IA complètes, plans d'implémentation concrets et architectures système.",
    challenge:
      "Civitta, a leading international consulting firm, aimed to equip startups in its Challenger AI Accelerator with robust AI product strategies and clear implementation roadmaps, enabling them to build sustainable competitive advantages in the Generative AI landscape.",
    solution:
      "We developed comprehensive AI product strategies and actionable implementation plans for four startups across three countries (Ukraine, Estonia, Brazil). This included detailed system architecture designs for secure, scalable, and efficient Generative AI deployment.",
    impact: [
      "All four startup teams solved their AI technology challenges",
      "Delivered AI product strategies with go-to-market plans",
      "Created scalable GenAI system architecture blueprints",
      "Multi-cloud deployment blueprints for each startup",
    ],
    technologies: [
      "AI Product Strategy",
      "GenAI Architecture",
      "Multi-Cloud Deployment",
      "Go-to-Market Frameworks",
    ],
    quote:
      "It was impressive how Shelpuk AI Technology Consulting found an individual approach to each team they worked with!",
    quoteAuthor: "Olena Shershun",
    quoteRole: "Associate Partner, Civitta",
    rating: 5.0,
  },
  {
    slug: "xfusion-rag-chatbot",
    title: "RAG Chatbot for Enterprise Data Platform",
    titleDe: "RAG-Chatbot für eine Enterprise-Datenplattform",
    titleFr: "Chatbot RAG pour données d'entreprise",
    client: "xFusion Technologies",
    clientTitle: "AI Data Management Corporation",
    industry: "Enterprise & Government",
    location: "CA, USA",
    duration: "Aug 2023 – Sep 2023",
    services: ["AI Development"],
    tags: ["AI Development", "RAG", "Enterprise & Government"],
    tagsDe: ["KI-Entwicklung", "RAG", "Enterprise & öffentlicher Sektor"],
    tagsFr: ["Développement IA", "RAG", "Entreprise & secteur public"],
    summary:
      "Engineered an advanced Retrieval-Augmented Generation (RAG) system for xFusion's xAQUA® platform, enabling natural language querying across enterprise document repositories.",
    summaryDe:
      "Entwickelt wurde ein fortgeschrittenes Retrieval-Augmented-Generation-System für xFusions xAQUA®-Plattform, das Abfragen in natürlicher Sprache über Unternehmensdokumente ermöglicht.",
    summaryFr:
      "Conception d'un système avancé de Retrieval-Augmented Generation pour la plateforme xAQUA® de xFusion, permettant des requêtes en langage naturel sur des référentiels documentaires d'entreprise.",
    challenge:
      "xFusion Technologies needed to build their xAQUA® AI platform to enable users to instantly retrieve precise, contextually relevant answers from complex enterprise document repositories in DOCX, PDF, and TXT formats.",
    solution:
      "We designed and implemented a Langchain-based document retrieval system integrated with Postgres pgvector database. The solution supports OpenAI's ChatGPT and GPT-4 LLMs, along with plug-and-play integration for Llama-2, Mistral, and other open-access LLMs.",
    impact: [
      "Drastically reduced time to find critical information",
      "Enabled more informed decisions based on quickly accessible data",
      "Elevated competitive edge of the xAQUA® platform",
      "Delivered as a versatile Python wheel package for easy integration",
    ],
    technologies: ["LangChain", "Postgres pgvector", "OpenAI", "Llama-2", "Mistral", "Python"],
    quote:
      "Their skill, spirit to build the right solution, sense of ownership, and responsiveness are all impressive.",
    quoteAuthor: "Sanjib Nayak",
    quoteRole: "Founder & CEO, xFusion Technologies",
    rating: 5.0,
  },
  {
    slug: "periodix-lead-generation-ai",
    title: "AI Sales Assistant for Lead Generation",
    titleDe: "KI-Vertriebsassistent für Lead-Generierung",
    titleFr: "Assistant IA pour la génération de leads",
    client: "Periodix",
    clientTitle: "Sales Enablement Platform",
    industry: "Sales & Marketing",
    location: "NYC, USA",
    duration: "May 2023 – Aug 2023",
    services: ["AI Development"],
    tags: ["AI Development", "Generative AI", "LLM Fine-Tuning", "Sales & Marketing"],
    tagsDe: ["KI-Entwicklung", "Generative KI", "LLM-Fine-Tuning", "Vertrieb & Marketing"],
    tagsFr: ["Développement IA", "IA générative", "Fine-tuning de LLM", "Ventes & marketing"],
    summary:
      "Developed a custom generative AI sales assistant chatbot using a fine-tuned Llama-2 model, deployed as a product-grade cloud component for lead generation.",
    summaryDe:
      "Entwickelt wurde ein individueller generativer KI-Vertriebsassistent auf Basis eines feinabgestimmten Llama-2-Modells, bereitgestellt als produktionsreife Cloud-Komponente für die Lead-Generierung.",
    summaryFr:
      "Développement d'un assistant commercial IA génératif sur mesure, basé sur un modèle Llama-2 fine-tuné et déployé comme composant cloud prêt pour la production afin de soutenir la génération de leads.",
    challenge:
      "Periodix sought to significantly enhance their lead generation capabilities by developing a highly effective, custom AI Sales Assistant Chatbot that could engage prospects with personalized interactions.",
    solution:
      "We developed and trained a custom Llama-2 conversational model on successful sales dialogues, coupled with a flexible deployment model for seamless upgrades and adaptability. Delivered as a product-grade cloud component.",
    impact: [
      "More effective and tailored engagement with potential leads",
      "Improved capture of high-intent prospects",
      "Easy AI model upgrades and swaps for long-term effectiveness",
      "New feature release delivered on time",
    ],
    technologies: ["Llama-2", "HuggingFace", "LangChain", "Conversational AI", "Cloud Deployment"],
    quote: "They had the skills to talk about complex things in the easiest way.",
    quoteAuthor: "Danil Yasynskyi",
    quoteRole: "Founder & CEO, Periodix",
    rating: 5.0,
  },
  {
    slug: "dynamiq-genai-platform-strategy",
    title: "Generative AI Strategy for AI Platform",
    titleDe: "Generative-AI-Strategie für eine KI-Plattform",
    titleFr: "Stratégie IA générative pour une plateforme IA",
    client: "Dynamiq",
    clientTitle: "Generative AI App Platform",
    industry: "Enterprise & Government",
    location: "CA, USA",
    duration: "Feb 2024 – Jun 2024",
    services: ["AI Consulting", "AI Development"],
    tags: ["AI Strategy", "Generative AI", "LLM Fine-Tuning", "Enterprise & Government"],
    tagsDe: ["KI-Strategie", "Generative KI", "LLM-Fine-Tuning", "Enterprise & öffentlicher Sektor"],
    tagsFr: ["Stratégie IA", "IA générative", "Fine-tuning de LLM", "Entreprise & secteur public"],
    summary:
      "Developed a comprehensive generative AI strategy including market analysis, LLM fine-tuning, and RAG integration for a leading generative AI platform.",
    summaryDe:
      "Entwickelt wurde eine umfassende Generative-AI-Strategie mit Marktanalyse, LLM-Fine-Tuning und RAG-Integration für eine führende Plattform im Bereich generative KI.",
    summaryFr:
      "Développement d'une stratégie complète d'IA générative incluant analyse de marché, fine-tuning de LLM et intégration RAG pour une plateforme leader du secteur.",
    challenge:
      "Dynamiq, a premier Generative AI platform, sought an advanced AI strategy to further enhance product offerings, deepen user engagement, and boost operational efficiency in the evolving AI landscape.",
    solution:
      "We devised and implemented a sophisticated AI strategy involving the design and development of unique generative AI models (Llama 3 & Mistral), meticulously fine-tuned with Dynamiq's proprietary data. This included market analysis, competitor benchmarking, and RAG integration.",
    impact: [
      "Enhanced product offerings with state-of-the-art generative AI capabilities",
      "Increased user engagement and satisfaction through context-aware responses",
      "Streamlined data retrieval processes, reducing time and resources",
      "Improved overall operational efficiency",
    ],
    technologies: [
      "Llama 3",
      "Mistral",
      "RAG",
      "LLM Fine-Tuning",
      "AI Product Strategy",
    ],
    quote:
      "The team's strategic insight, technical prowess, and support have been instrumental in the success of our initiatives.",
    quoteAuthor: "Vitalii Duk",
    quoteRole: "Founder & CEO, Dynamiq",
    rating: 5.0,
  },
  {
    slug: "axbio-questionnaire-analysis",
    title: "AI-Powered Health Questionnaire Analysis Platform",
    titleDe: "KI-Plattform für Gesundheitsfragebögen",
    titleFr: "Analyse de questionnaires de santé par IA",
    client: "Axbio",
    clientTitle: "Health & Wellness Technology Company",
    industry: "Pharma & Healthcare",
    location: "USA",
    duration: "Oct 2024 – Dec 2024",
    services: ["AI Development"],
    tags: ["AI Development", "Generative AI", "Pharma & Healthcare"],
    tagsDe: ["KI-Entwicklung", "Generative KI", "Pharma & Gesundheitswesen"],
    tagsFr: ["Développement IA", "IA générative", "Pharma & santé"],
    summary:
      "Built an end-to-end cloud-based platform that collects health questionnaire responses, applies weighted scoring, and uses Azure OpenAI to generate personalized health reports delivered automatically via email.",
    summaryDe:
      "Entwickelt wurde eine cloudbasierte End-to-End-Plattform, die Gesundheitsfragebögen erfasst, gewichtet auswertet und mit Azure OpenAI personalisierte Gesundheitsberichte automatisch per E-Mail erstellt.",
    summaryFr:
      "Conception d'une plateforme cloud de bout en bout collectant des réponses à des questionnaires de santé, appliquant un scoring pondéré et utilisant Azure OpenAI pour générer des rapports personnalisés envoyés automatiquement par e-mail.",
    challenge:
      "Axbio needed a scalable, automated system to collect health-related questionnaire data from users, analyze responses with a weighted scoring model, and deliver personalized health reports — all without requiring technical expertise to manage or update the questionnaire content.",
    solution:
      "We designed and built a cloud-native pipeline using Microsoft Forms for questionnaire management, Power Automate for answer routing, and a Python microservice on Azure Container Apps for processing. Azure OpenAI generates personalized health reports based on weighted response analysis, and Azure Communication Services delivers them via email. The system allows non-technical administrators to easily add, edit, or remove questions and adjust scoring weights.",
    impact: [
      "Fully automated pipeline from questionnaire submission to personalized report delivery",
      "Non-technical staff can manage questions and scoring without developer involvement",
      "AI-generated personalized health reports for each respondent",
      "Scalable cloud architecture on Azure Container Apps",
    ],
    technologies: ["Azure OpenAI", "Azure Container Apps", "Python", "Microsoft Power Automate", "Azure Communication Services", "Docker"],
    quote: "The system allows us to deliver personalized health insights at scale with minimal manual effort.",
    quoteAuthor: "Nikolay Sergeev",
    quoteRole: "VP, Applications at Axbio",
    rating: 5.0,
  },
  {
    slug: "pumb-retail-banking-ai-strategy",
    title: "AI Strategy for Retail Banking",
    titleDe: "KI-Strategie für Retail Banking",
    titleFr: "Stratégie IA pour la banque de détail",
    client: "Major Retail Bank",
    clientTitle: "Major Retail Bank",
    industry: "Banking & Finance",
    location: "Ukraine",
    duration: "Jun 2021 – Aug 2021",
    services: ["AI Consulting"],
    tags: ["AI Strategy", "Banking & Finance"],
    tagsDe: ["KI-Strategie", "Banken & Finanzen"],
    tagsFr: ["Stratégie IA", "Banque & finance"],
    summary:
      "Designed a comprehensive AI strategy for a major retail bank, including competitive analysis, AI model recommendations, and a technology transformation roadmap.",
    summaryDe:
      "Entwickelt wurde eine umfassende KI-Strategie für eine große Retailbank, einschließlich Wettbewerbsanalyse, Empfehlungen für KI-Modelle und einer Roadmap zur technologischen Transformation.",
    summaryFr:
      "Conception d'une stratégie IA complète pour une grande banque de détail, incluant analyse concurrentielle, recommandations de modèles IA et feuille de route de transformation technologique.",
    challenge:
      "A major retail bank needed a comprehensive AI strategy as part of their business strategy design. They required analysis and recommendations for AI models across Impact, Feasibility, Cost, and Sustainable Advantage dimensions.",
    solution:
      "We designed the AI strategy including competitive AI analysis, gap analysis, AI practices assessment, strategic options evaluation with guerrilla testing, and recommendations. We also led the customer team in developing AI models for Marketing and CRM (CLV, behavioral targeting, A/B testing, personalized recommendations).",
    impact: [
      "Most recommendations were implemented or implementation started",
      "AI/Data Science team formed and staffed per recommendations",
      "Conversion rates and financial metrics on credits improved",
      "Marketing and CRM model metrics improved significantly",
      "New models launched per Impact/Investment matrix priorities",
    ],
    technologies: [
      "AI Strategy Frameworks",
      "CLV Modeling",
      "Behavioral Targeting",
      "A/B Testing",
      "Personalized Recommendations",
    ],
    quote:
      "They had the talent to keep positive momentum with both to-the-point communication and humor when necessary.",
    quoteAuthor: "Andriy Milinevskyy, MBA",
    quoteRole: "Founder & CEO, CXdesign",
    rating: 5.0,
  },
  {
    slug: "bank-rag-chatbot",
    title: "RAG Chatbot for Major Canadian Bank",
    titleDe: "RAG-Chatbot für eine große kanadische Bank",
    titleFr: "Chatbot RAG pour une grande banque canadienne",
    client: "Major Canadian Bank",
    clientTitle: "Major Canadian Bank",
    industry: "Banking & Finance",
    location: "Canada",
    duration: "Jan 2025 – Mar 2025",
    services: ["AI Development"],
    tags: ["AI Development", "RAG", "Generative AI", "Banking & Finance"],
    tagsDe: ["KI-Entwicklung", "RAG", "Generative KI", "Banken & Finanzen"],
    tagsFr: ["Développement IA", "RAG", "IA générative", "Banque & finance"],
    summary:
      "Built a production-ready Retrieval-Augmented Generation (RAG) system enabling intelligent question-answering across 3,200+ internal documents using Azure OpenAI and LangChain.",
    summaryDe:
      "Entwickelt wurde ein produktionsreifes Retrieval-Augmented-Generation-System, das mit Azure OpenAI und LangChain intelligente Fragen-Antworten über mehr als 3.200 interne Dokumente ermöglicht.",
    summaryFr:
      "Conception d'un système de Retrieval-Augmented Generation prêt pour la production, permettant des réponses intelligentes sur plus de 3 200 documents internes grâce à Azure OpenAI et LangChain.",
    challenge:
      "The bank needed to enable employees and stakeholders to quickly find accurate, context-aware answers from a vast corpus of internal documents spanning multiple business areas. Manual search was slow and often returned incomplete or irrelevant results.",
    solution:
      "We designed and delivered a complete RAG system consisting of a Python library with DocumentManager and ConversationManager components, a containerized Flask microservice with RESTful APIs, and a data loading utility for ingesting 3,200+ documents. The system supports multi-turn conversations, image-aware document processing, and multiple conversation modes (auto, context-only, general-only).",
    impact: [
      "3,200+ documents ingested and searchable via semantic search",
      "Multi-turn conversation support with context-aware responses",
      "Image analysis for graphs and visual elements in documents",
      "Production-ready containerized microservice with Docker deployment",
    ],
    technologies: ["Azure OpenAI", "LangChain", "PGVector", "Flask", "Docker", "Python", "Ragas"],
    rating: 5.0,
  },
];

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return caseStudies.find((cs) => cs.slug === slug);
}

export function getCaseStudiesByTag(tag: string): CaseStudy[] {
  return caseStudies.filter((cs) => cs.tags.includes(tag));
}

export function getLocalizedCaseStudyField(
  caseStudy: CaseStudy,
  field: "title" | "summary",
  lang: string,
): string {
  const safeLang = normalizeCaseStudyLanguage(lang);

  if (safeLang === "de") {
    const deKey = `${field}De` as "titleDe" | "summaryDe";
    return caseStudy[deKey] || caseStudy[field];
  }

  if (safeLang === "fr") {
    const frKey = `${field}Fr` as "titleFr" | "summaryFr";
    return caseStudy[frKey] || caseStudy[field];
  }

  if (safeLang === "it") {
    const inlineKey = `${field}It` as "titleIt" | "summaryIt";
    const metadata = caseStudyItalianMetadata[caseStudy.slug];
    return metadata?.[field] || caseStudy[inlineKey] || caseStudy[field];
  }

  if (safeLang === "es") {
    const metadata = caseStudySpanishMetadata[caseStudy.slug];
    return metadata?.[field] || caseStudy[field];
  }

  return caseStudy[field];
}

export function getLocalizedCaseStudyTags(caseStudy: CaseStudy, lang: string): string[] {
  const safeLang = normalizeCaseStudyLanguage(lang);

  if (safeLang === "de" && caseStudy.tagsDe?.length) {
    return caseStudy.tagsDe;
  }

  if (safeLang === "fr" && caseStudy.tagsFr?.length) {
    return caseStudy.tagsFr;
  }

  if (safeLang === "it") {
    return caseStudyItalianMetadata[caseStudy.slug]?.tags || caseStudy.tagsIt || caseStudy.tags;
  }

  if (safeLang === "es") {
    return caseStudySpanishMetadata[caseStudy.slug]?.tags || caseStudy.tags;
  }

  return caseStudy.tags;
}

export function getLocalizedCaseStudyTag(tag: string, lang: string): string {
  const safeLang = normalizeCaseStudyLanguage(lang);
  const localized = CASE_STUDY_TAG_LABELS[tag as CaseStudyTag];
  return localized?.[safeLang] || tag;
}

export function localizeCaseStudy(caseStudy: CaseStudy, lang: string): LocalizedCaseStudy {
  const safeLang = normalizeCaseStudyLanguage(lang);
  const germanDetails = safeLang === "de" ? caseStudyGermanDetails[caseStudy.slug] : undefined;
  const frenchDetails = safeLang === "fr" ? caseStudyFrenchDetails[caseStudy.slug] : undefined;
  const italianDetails = safeLang === "it" ? caseStudyItalianDetails[caseStudy.slug] : undefined;
  const spanishDetails = safeLang === "es" ? caseStudySpanishDetails[caseStudy.slug] : undefined;

  return {
    ...caseStudy,
    title: getLocalizedCaseStudyField(caseStudy, "title", lang),
    summary: getLocalizedCaseStudyField(caseStudy, "summary", lang),
    tags: getLocalizedCaseStudyTags(caseStudy, lang),
    ...(germanDetails || frenchDetails || italianDetails || spanishDetails || {}),
  };
}

export function getLocalizedCaseStudyBySlug(
  slug: string,
  lang: string,
): LocalizedCaseStudy | undefined {
  const caseStudy = getCaseStudyBySlug(slug);
  return caseStudy ? localizeCaseStudy(caseStudy, lang) : undefined;
}
